import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getParsedValue } from './utils';
import { IUnderwritingCoverage } from '@converge/graphql';

interface UseRaterCoverageUpdatesProps {
  parentCoverage?: IUnderwritingCoverage;
  updateCoverage: (
    coverage: IUnderwritingCoverage & { update: boolean }
  ) => void;
  nameForError?: string | null;
  limitField: string;
  retentionField: string;
  limitValue?: number | null;
  retentionValue?: number | null;
  coverage: IUnderwritingCoverage;
  setLimitErrorValue: (value: number | null) => void;
}

export const useRaterCoverageUpdates = ({
  parentCoverage,
  updateCoverage,
  nameForError,
  limitField,
  retentionField,
  limitValue,
  coverage,
  retentionValue,
  setLimitErrorValue,
}: UseRaterCoverageUpdatesProps) => {
  const [currentLimit, setCurrentLimit] = useState<number | null>(null);
  const [retentionErrorValue, setRetentionErrorValue] = useState<number | null>(
    null
  );
  const [currentRetention, setCurrentRetention] = useState<number | null>(null);
  const [coverageLimitErrorValue, setParentCoverageLimit] = useState<
    number | null
  >(null);
  const [parentCoverageRetentionValue, setParentCoverageRetention] = useState<
    number | null
  >(null);
  const { setError, clearErrors } = useFormContext();

  const {
    name,
    quote: {
      minFusionScoreModifier,
      maxFusionScoreModifier,
      selectedFusionScoreModifier,
    } = {},
  } = coverage;

  const checkForNullValue = useCallback(
    (fieldName: string, value?: number | null) => {
      if (!value) {
        setError(`${fieldName}.${nameForError}`, {
          type: 'fieldName',
          message: `${fieldName} must be greater than 1`,
        });
        return true;
      }
      return false;
    },
    [nameForError, setError]
  );

  const handleUpdateLimit = useCallback(
    (newValue: number | null) => {
      setCurrentLimit(newValue);
      if (newValue === limitValue || checkForNullValue('subLimit', newValue)) {
        clearErrors(`subLimit.${nameForError}`);
        setLimitErrorValue(null);
        return;
      }
      if (!parentCoverage) {
        setParentCoverageLimit(newValue);
      } else {
        clearErrors(`subLimit.${nameForError}`);
        setLimitErrorValue(null);
      }

      updateCoverage({
        ...coverage,
        quote: {
          ...coverage.quote,
          [limitField]: newValue,
        },
        update: true,
      });
    },
    [
      checkForNullValue,
      clearErrors,
      coverage,
      limitField,
      limitValue,
      nameForError,
      parentCoverage,
      updateCoverage,
      setLimitErrorValue,
    ]
  );

  const handleUpdateRetention = useCallback(
    (newValue: number | null) => {
      setCurrentRetention(newValue);

      if (
        newValue === retentionValue ||
        checkForNullValue('subRetention', newValue)
      ) {
        clearErrors(`subRetention.${nameForError}`);
        setRetentionErrorValue(null);
        return;
      } else if (!parentCoverage) {
        setParentCoverageRetention(newValue);
      } else {
        clearErrors(`subRetention.${nameForError}`);
        setRetentionErrorValue(null);
      }

      updateCoverage({
        ...coverage,
        quote: {
          ...coverage.quote,
          [retentionField]: newValue,
        },
        update: true,
      });
    },
    [
      checkForNullValue,
      clearErrors,
      coverage,
      nameForError,
      parentCoverage,
      retentionField,
      retentionValue,

      updateCoverage,
    ]
  );

  const handleUpdateFusionScoreModifier = useCallback(
    (newValue: number | null) => {
      const parsedValue = getParsedValue(newValue);
      if (
        parsedValue !== null &&
        parsedValue !== undefined &&
        parsedValue < (minFusionScoreModifier || 0)
      ) {
        setError(`selectedFusionScoreModifier.${nameForError}`, {
          type: 'FusionScoreModifier',
          message: `Fusion Score modifier must be a number between ${
            minFusionScoreModifier ?? 0
          } and ${maxFusionScoreModifier ?? 100}`,
        });
        return;
      }
      if (
        parsedValue !== null &&
        parsedValue !== undefined &&
        parsedValue > (maxFusionScoreModifier || 100)
      ) {
        setError(`selectedFusionScoreModifier.${nameForError}`, {
          type: 'FusionScoreModifier',
          message: `Fusion Score modifier must be a number between ${
            minFusionScoreModifier ?? 0
          } and ${maxFusionScoreModifier ?? 100}`,
        });
        return;
      }

      clearErrors(`selectedFusionScoreModifier.${name}`);

      if (parsedValue === selectedFusionScoreModifier) {
        return;
      }

      updateCoverage({
        ...coverage,
        quote: {
          ...coverage.quote,
          selectedFusionScoreModifier: parsedValue,
        },
        update: true,
      });
    },
    [
      clearErrors,
      coverage,
      maxFusionScoreModifier,
      minFusionScoreModifier,
      name,
      nameForError,
      selectedFusionScoreModifier,
      setError,
      updateCoverage,
    ]
  );

  return {
    handleUpdateLimit,
    handleUpdateRetention,
    handleUpdateFusionScoreModifier,
    retentionErrorValue,
    currentLimit,
    currentRetention,
    coverageLimitErrorValue,
    parentCoverageRetentionValue,
  };
};
